import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { API_URLS } from "src/api/api-urls";
import useApiServices from "src/api/use-api-services";
import AppButton from "src/components/common/app-button";
import Iconify from "src/components/iconify";
import { useBoolean } from "src/hooks/use-boolean";
import { IScrapingParamsDto } from "src/pages/dashboard/custom/type";
import { IPlatformsListDto } from "src/types/platforms";

type NewReportFormDialogPropsType = {
  open: boolean;
  onClose: VoidFunction;
  onCloseReportsDialog: VoidFunction;
};

const formDefaultValues = {
  keyword: "",
  platform: "",
  start_date: moment().format("yyyy-MM-DD"),
  end_date: moment().format("yyyy-MM-DD"),
};

const NewReportFormDialog = ({
  onClose,
  open,
  onCloseReportsDialog,
}: NewReportFormDialogPropsType) => {
  // #region States and hooks
  const submit = useBoolean();
  const queryClient = useQueryClient();

  const [form, setForm] = useState<IScrapingParamsDto>(formDefaultValues);

  const { useGetListApi } = useApiServices();
  // #endregion

  // #region Services
  const { data: platforms, isLoading: isLoadingPlatforms } = useGetListApi<IPlatformsListDto>({
    url: API_URLS.PLATFORMS,
  });

  const { error } = useGetListApi({
    url: API_URLS.custom.SCRAPING,
    enabled: submit.value,
    parameters: form as IScrapingParamsDto,
    onSuccess: () => {
      console.log("success");
      submit.onFalse();
      onClose();
      onCloseReportsDialog();
      enqueueSnackbar({
        message: "You will be notified when your report is ready",
        variant: "info",
      });

      queryClient.invalidateQueries({ queryKey: [API_URLS.ACTIVITY + "list" + "pages"] });
      setForm(formDefaultValues);
    },
    onError: (error) => {
      console.log("error", error);
    },
  });
  // #endregion

  // #region Handlers
  const handleSubmit = () => {
    submit.onTrue();
  };
  // #endregion

  useEffect(() => {
    error && enqueueSnackbar(error?.response?.data?.errors?.[0]?.message, { variant: "error" });
  }, [error]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">New Report</Typography>
          <IconButton onClick={onClose}>
            <Iconify icon="eva:close-fill" sx={{ cursor: "pointer" }} />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3, paddingTop: "8px !important" }}>
        <Stack spacing={2}>
          <TextField
            label="Keyword"
            value={form.keyword}
            onChange={(e) => setForm((preValue) => ({ ...preValue, keyword: e.target.value }))}
            fullWidth
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <DatePicker
                label="Start Date"
                value={form.start_date}
                onChange={(e) =>
                  setForm((preValue) => ({
                    ...preValue,
                    start_date: moment(e).format("yyyy-MM-DD") || "",
                  }))
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
              <DatePicker
                label="End Date"
                value={form.end_date}
                onChange={(e) =>
                  setForm((preValue) => ({
                    ...preValue,
                    end_date: moment(e).format("yyyy-MM-DD") || "",
                  }))
                }
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Stack>
          </LocalizationProvider>

          <FormControl>
            <InputLabel id="platform-label">Platform</InputLabel>
            <Select
              labelId="platform-label"
              value={form.platform}
              onChange={(e) => setForm((preValue) => ({ ...preValue, platform: e.target.value }))}
              label="Platform"
            >
              {platforms?.platforms.map((platform) =>
                platform.data.map(
                  (item) =>
                    item.label && (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    )
                )
              )}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} alignItems="center">
            <AppButton label="Cancel" onClick={onClose} color="error" variant="outlined" />
            <AppButton
              label="Save"
              onClick={handleSubmit}
              disabled={Object.values(form).some((value) => !value)}
            />
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default NewReportFormDialog;
